import React, { useState } from 'react';
import { Button, Card, EmptyValue, Form, Modal, StatusBadge, Table } from '@/components/ui';
import { VehicleInsurance } from '../../../types/Vehicle';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import { formatDate } from '../../../utils/wrapDate';

interface EquipmentInsuranceCardProps {
  vehicleId: number;
  insurances: VehicleInsurance[];
  /** Edit mode with vehicle:edit: add and remove policies (EQ-20). */
  canEdit?: boolean;
  /** Called after a policy is added or removed, so the page reloads the unit. */
  onChanged?: () => Promise<void> | void;
  now?: Date;
}

type PolicyState = 'Current' | 'Expired' | 'Upcoming';

export function policyState(policy: VehicleInsurance, now: Date = new Date()): PolicyState {
  if (new Date(policy.expires).getTime() < now.getTime()) return 'Expired';
  if (new Date(policy.effectiveFrom).getTime() > now.getTime()) return 'Upcoming';
  return 'Current';
}

const STATE_VARIANT = { Current: 'success', Expired: 'danger', Upcoming: 'info' } as const;

const EMPTY_POLICY = { policyNumber: '', insurer: '', effectiveFrom: '', expires: '' };

const EquipmentInsuranceCard: React.FC<EquipmentInsuranceCardProps> = ({
  vehicleId,
  insurances,
  canEdit = false,
  onChanged,
  now = new Date()
}) => {
  // Latest expiry first, so the policy that matters today is on top.
  const sorted = [...insurances].sort((a, b) => new Date(b.expires).getTime() - new Date(a.expires).getTime());
  const [adding, setAdding] = useState(false);
  const [policy, setPolicy] = useState(EMPTY_POLICY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<VehicleInsurance | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const addPolicy = async () => {
    const next: Record<string, string> = {};
    if (!policy.policyNumber.trim()) next.policyNumber = 'Policy # required';
    if (!policy.insurer.trim()) next.insurer = 'Insurer required';
    if (!policy.effectiveFrom) next.effectiveFrom = 'Effective From required';
    if (!policy.expires) next.expires = 'Expires required';
    else if (policy.effectiveFrom && policy.expires < policy.effectiveFrom) next.expires = "Expires can't be before Effective From";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    try {
      await vehicleService.addInsurance(vehicleId, policy);
      setPolicy(EMPTY_POLICY);
      setAdding(false);
      await onChanged?.();
    } catch (err) {
      setErrors(fieldErrorsOf(err));
    } finally {
      setBusy(false);
    }
  };

  const removePolicy = async () => {
    if (!removing) return;
    setBusy(true);
    setRemoveError(null);
    try {
      await vehicleService.deleteInsurance(vehicleId, removing.id);
      setRemoving(null);
      await onChanged?.();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Could not remove the policy. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const field = (key: keyof typeof EMPTY_POLICY, label: string, type = 'text') => (
    <div>
      <Form.Label htmlFor={`policy-${key}`}>
        {label} <span className="tw-text-danger" aria-hidden="true">*</span>
      </Form.Label>
      <Form.Control id={`policy-${key}`} type={type} value={policy[key]} onChange={(e) => setPolicy((p) => ({ ...p, [key]: e.target.value }))} />
      {errors[key] && <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">{errors[key]}</p>}
    </div>
  );

  return (
    <Card>
      <Card.Header className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Insurance</h2>
        {canEdit && !adding && (
          <Button size="sm" variant="outline-secondary" onClick={() => setAdding(true)}>
            <i className="bi bi-plus-lg tw-mr-1" aria-hidden="true" />
            Add Policy
          </Button>
        )}
      </Card.Header>
      <Card.Body className="tw-p-0">
        {adding && (
          <div className="tw-border-b tw-border-line tw-p-4">
            <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
              {field('policyNumber', 'Policy #')}
              {field('insurer', 'Insurer')}
              {field('effectiveFrom', 'Effective From', 'date')}
              {field('expires', 'Expires', 'date')}
            </div>
            {errors._form && <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-danger" role="alert">{errors._form}</p>}
            <div className="tw-mt-3 tw-flex tw-justify-end tw-gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={busy}
                onClick={() => {
                  setAdding(false);
                  setPolicy(EMPTY_POLICY);
                  setErrors({});
                }}
              >
                Cancel
              </Button>
              <Button size="sm" disabled={busy} onClick={addPolicy}>
                {busy ? 'Saving…' : 'Save Policy'}
              </Button>
            </div>
          </div>
        )}
        {sorted.length === 0 ? (
          <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">No insurance on file.</p>
        ) : (
          <Table responsive className="tw-mb-0">
            <thead>
              <tr>
                <th>Status</th>
                <th>Policy #</th>
                <th>Insurer</th>
                <th>Effective From</th>
                <th>Expires</th>
                <th>Document</th>
                {canEdit && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const state = policyState(row, now);
                return (
                  <tr key={row.id}>
                    <td>
                      <StatusBadge status={state} label={state} variant={STATE_VARIANT[state]} soft />
                    </td>
                    <td>{row.policyNumber}</td>
                    <td>{row.insurer}</td>
                    <td className="tw-whitespace-nowrap">{formatDate(row.effectiveFrom) ?? <EmptyValue />}</td>
                    <td className="tw-whitespace-nowrap">{formatDate(row.expires) ?? <EmptyValue />}</td>
                    <td>
                      {row.documentUrl ? (
                        <a href={vehicleService.insuranceDocumentUrl(vehicleId, row.id)} target="_blank" rel="noopener noreferrer">
                          View Policy
                        </a>
                      ) : (
                        <EmptyValue />
                      )}
                    </td>
                    {canEdit && (
                      <td className="tw-text-right">
                        <Button size="sm" variant="outline-danger" onClick={() => setRemoving(row)}>
                          Remove
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={removing !== null} onHide={() => setRemoving(null)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Remove Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-mb-0 tw-text-sm">
            Remove policy {removing?.policyNumber} from this unit? It is recorded in Audit History.
          </p>
          {removeError && <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-danger" role="alert">{removeError}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRemoving(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={removePolicy} disabled={busy}>
            {busy ? 'Removing…' : 'Remove Policy'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default EquipmentInsuranceCard;
